import React, { useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Form, InputGroup } from "react-bootstrap";

const DEFAULT_PGY_OPTIONS = Array.from({ length: 5 }, (_, i) => ({
  value: i + 1,
  label: `PGY-${i + 1}`,
}));

const ExpandableUserSelector = ({
  availableUsers = [],
  selectedUsers = [],
  onSelectionChange,
  pgyLevelOptions = [],
  /** When set (>0), PGY filter options cannot exceed this program max (also caps fallback defaults). */
  maxPGYLevel,
  /** Optional map keyed by program id → max PGY for that program (when filtering by institution program). */
  programMaxPGYById,
  isInstitutionalAdmin = false,
  programOptions = [],
  helperText = "Selected users will receive an invitation.",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedPGY, setSelectedPGY] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("");

  const programLookup = useMemo(() => {
    const map = new Map();
    for (const p of programOptions) {
      map.set(p.id, p.name);
    }
    return map;
  }, [programOptions]);

  const effectiveProgramMaxPGY = useMemo(() => {
    if (!isInstitutionalAdmin || !selectedProgram) return null;
    const pid = parseInt(selectedProgram, 10);
    if (Number.isNaN(pid) || !programMaxPGYById) return null;
    const fromMap =
      programMaxPGYById[pid] ?? programMaxPGYById[String(pid)];
    return typeof fromMap === "number" && Number.isFinite(fromMap) && fromMap > 0 ? fromMap : null;
  }, [isInstitutionalAdmin, selectedProgram, programMaxPGYById]);

  /** Prefer program-specific max (institutional + program filter); else parent's program-scoped max. */
  const effectiveMaxPGY = useMemo(() => {
    if (effectiveProgramMaxPGY != null) return effectiveProgramMaxPGY;
    if (typeof maxPGYLevel === "number" && Number.isFinite(maxPGYLevel) && maxPGYLevel > 0) {
      return Math.floor(maxPGYLevel);
    }
    return null;
  }, [effectiveProgramMaxPGY, maxPGYLevel]);

  const effectivePgyOptions = useMemo(() => {
    const baseHas = pgyLevelOptions.length > 0;
    const base = baseHas ? pgyLevelOptions : DEFAULT_PGY_OPTIONS;
    if (effectiveMaxPGY == null) {
      return baseHas ? base : DEFAULT_PGY_OPTIONS;
    }
    const cap = effectiveMaxPGY;
    const fromBase =
      baseHas ? pgyLevelOptions.filter((opt) => Number(opt.value) <= cap) : [];
    const highestInBase =
      fromBase.length > 0 ? Math.max(...fromBase.map((o) => Number(o.value))) : 0;
    if (fromBase.length > 0 && highestInBase >= cap) return fromBase;
    const src = baseHas ? pgyLevelOptions : [];
    return Array.from({ length: cap }, (_, i) => {
      const v = i + 1;
      const match = src.find((o) => Number(o.value) === v);
      return match ?? { value: v, label: `PGY-${v}` };
    });
  }, [pgyLevelOptions, effectiveMaxPGY]);

  useEffect(() => {
    if (selectedRole !== "resident" || !selectedPGY) return;
    const n = Number(selectedPGY);
    if (!Number.isFinite(n)) return;
    const allowed = effectivePgyOptions.some((o) => Number(o.value) === n);
    if (!allowed) setSelectedPGY("");
  }, [selectedRole, selectedPGY, effectivePgyOptions]);

  const filteredUsers = useMemo(() => {
    let filtered = availableUsers;

    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          (user.firstName || "").toLowerCase().includes(query) ||
          (user.lastName || "").toLowerCase().includes(query) ||
          (user.email || "").toLowerCase().includes(query)
      );
    }

    if (selectedRole) {
      filtered = filtered.filter((user) => user.role === selectedRole);
    }

    if (selectedRole === "resident" && selectedPGY) {
      const pgyValue = parseInt(selectedPGY, 10);
      filtered = filtered.filter((user) => user.year === pgyValue);
    }

    if (isInstitutionalAdmin && selectedProgram) {
      const pid = parseInt(selectedProgram, 10);
      filtered = filtered.filter(
        (user) => (user.programId ?? user.program?.id) === pid
      );
    }

    return filtered;
  }, [
    availableUsers,
    searchTerm,
    selectedRole,
    selectedPGY,
    isInstitutionalAdmin,
    selectedProgram,
  ]);

  const selectedUsersLookup = useMemo(
    () => new Set(selectedUsers),
    [selectedUsers]
  );

  const selectedVisibleUsers = useMemo(
    () => availableUsers.filter((user) => selectedUsersLookup.has(user.id)),
    [availableUsers, selectedUsersLookup]
  );

  const handleUserSelection = (userId) => {
    const updatedSelection = selectedUsersLookup.has(userId)
      ? selectedUsers.filter((id) => id !== userId)
      : [...selectedUsers, userId];
    onSelectionChange(updatedSelection);
  };

  const areAllFilteredUsersSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every((user) => selectedUsersLookup.has(user.id));

  const handleSelectAllToggle = () => {
    const filteredIds = filteredUsers.map((u) => u.id);
    if (areAllFilteredUsersSelected) {
      onSelectionChange(selectedUsers.filter((id) => !filteredIds.includes(id)));
      return;
    }

    const merged = [...selectedUsers];
    filteredIds.forEach((id) => {
      if (!merged.includes(id)) {
        merged.push(id);
      }
    });
    onSelectionChange(merged);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedRole("");
    setSelectedPGY("");
    setSelectedProgram("");
    setShowFilterDropdown(false);
  };

  const fieldText =
    selectedVisibleUsers.length > 0
      ? `${selectedVisibleUsers.length} user${selectedVisibleUsers.length === 1 ? "" : "s"} selected`
      : "Click to select and add a user";

  return (
    <Form.Group>
      <Dropdown
        show={isExpanded}
        onToggle={(isOpen) => setIsExpanded(isOpen)}
        autoClose="outside"
      >
        <Dropdown.Toggle
          as="button"
          type="button"
          id="expandable-user-selector-toggle"
          className="btn btn-secondary w-100 text-start d-flex align-items-center justify-content-between border rounded bg-white px-3 py-2 mt-05"
          style={{ minHeight: 58}}
        >
          <span className={` text-truncate ${selectedVisibleUsers.length ? "" : "text-muted"}`}>{fieldText}</span>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="w-100 mt-1 p-0 border rounded"
          style={{ minWidth: "100%" }}
        >
   

          <InputGroup className="mb-2 border-bottom">
            <Form.Control
              type="text"
              placeholder="Search User"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-0 rounded-end-0"
            />
            <Dropdown
              show={showFilterDropdown}
              onToggle={(isOpen) => setShowFilterDropdown(isOpen)}
              align="end"
              autoClose={false}
            >
              <Dropdown.Toggle
                as={Button}
                type="button"
                id="expandable-user-filter-dropdown-toggle"
                variant="link"
                className="no-arrow rounded-0"
              >
                <i className="bi bi-list" />
              </Dropdown.Toggle>
              <Dropdown.Menu
                className="p-3 border-0"
                style={{ width: "100%", maxHeight: "300px", overflowY: "auto", zIndex: 1000 }}
              >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-semibold">Filter</span>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="p-0 text-muted text-decoration-none"
                      onClick={() => setShowFilterDropdown(false)}
                      aria-label="Close filter"
                    >
                      <i className="bi bi-x-lg" />
                    </Button>
                  </div>
                  <Form.Group className="mb-2">
                    <Form.Label className="mb-1">Role</Form.Label>
                    <Form.Select
                      value={selectedRole}
                      onChange={(e) => {
                        setSelectedRole(e.target.value);
                        if (e.target.value !== "resident") {
                          setSelectedPGY("");
                        }
                      }}
                    >
                      <option value="">All Roles</option>
                      <option value="resident">Residents</option>
                      <option value="attending">Attendings</option>
                      <option value="director">Directors</option>
                      <option value="medical student">Medical Students</option>
                      <option value="administrative assistant">Administrative Assistants</option>
                    </Form.Select>
                  </Form.Group>
                  {selectedRole === "resident" && (
                    <Form.Group className="mb-2">
                      <Form.Label className="mb-1">PGY Level</Form.Label>
                      <Form.Select
                        value={selectedPGY}
                        onChange={(e) => setSelectedPGY(e.target.value)}
                      >
                        <option value="">All</option>
                        {effectivePgyOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}
                  {isInstitutionalAdmin && programOptions.length > 0 && (
                    <Form.Group className="mb-2">
                      <Form.Label className="mb-1">Program</Form.Label>
                      <Form.Select
                        value={selectedProgram}
                        onChange={(e) => setSelectedProgram(e.target.value)}
                      >
                        <option value="">All Programs</option>
                        {programOptions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}
                  <div className="d-flex gap-2">
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      className="flex-fill"
                      onClick={() => setShowFilterDropdown(false)}
                      disabled={!selectedRole && !selectedPGY && !selectedProgram}
                    >
                      Apply Filter
                    </Button>
                    <Button
                      type="button"
                      variant="light"
                      size="sm"
                      className="flex-fill"
                      onClick={clearFilters}
                      disabled={!searchTerm && !selectedRole && !selectedPGY && !selectedProgram}
                    >
                      Clear
                    </Button>
                  </div>
              </Dropdown.Menu>
            </Dropdown>
          </InputGroup>
          
          {selectedVisibleUsers.length > 0 ? (
            <div className="d-flex flex-wrap gap-1 mb-1 px-2">
              {selectedVisibleUsers.slice(0, 8).map((user) => (
                <span
                  key={user.id}
                  className="badge rounded bg-secondary d-flex align-items-center gap-1 p-1 py-0"
                >
                  <span className="fw-normal">
                    {user.firstName} {user.lastName}
                  </span>
                  <Button
                    variant="link-dng"
                    size="sm"
                    className="p-0"
                    onClick={() => handleUserSelection(user.id)}
                    title="Remove user"
                  >
                    <i className="bi bi-x" />
                  </Button>
                </span>
              ))}
              {selectedVisibleUsers.length > 8 && (
                <span className="text-muted small align-self-center">
                  +{selectedVisibleUsers.length - 8} more
                </span>
              )}
            </div>
          ) : (
            <div className="text-muted small px-2 mb-1">No user selected</div>
          )}

          {(searchTerm || selectedRole || selectedPGY || selectedProgram) && (
            <div className="mt-2 px-2">
              <small className="text-muted">
                Showing {filteredUsers.length} of {availableUsers.length} users
                {searchTerm && ` matching "${searchTerm}"`}
                {selectedRole && ` with role "${selectedRole}"`}
                {selectedPGY && ` at PGY-${selectedPGY} level`}
                {selectedProgram &&
                  ` in ${programLookup.get(parseInt(selectedProgram, 10)) || "selected program"}`}
              </small>
            </div>
          )}

          <div
            className="border-top border-bottom bg-white mt-2"
            style={{ maxHeight: "220px", overflowY: "auto" }}
          >
            {filteredUsers.length > 0 ? (
              <>
                <div className="form-check border-bottom py-2 px-3 mb-2">
                  <Form.Check
                    type="checkbox"
                    id="expandable-select-all-users"
                    checked={areAllFilteredUsersSelected}
                    onChange={handleSelectAllToggle}
                    label={
                      <span className="fw-semibold">
                        {areAllFilteredUsersSelected ? "Deselect All" : "Select All"}
                        <span className="text-muted ms-2">({filteredUsers.length} users)</span>
                      </span>
                    }
                  />
                </div>
                {filteredUsers.map((user) => {
                  const isSelected = selectedUsersLookup.has(user.id);
                  return (
                    <button
                      key={user.id}
                      type="button"
                      className="w-100 text-start border-0 px-3 py-1 d-block custom-hover-bg-alt"
                      onClick={() => handleUserSelection(user.id)}
                      style={{
                        backgroundColor: isSelected ? "var(--blue-light)" : "transparent",
                      }}
                    >
                      <div>
                          <span className="fw-semibold">{user.firstName} {user.lastName}</span>
                        <span className="text-muted ms-2">({user.role})</span>
                        {user.specialty && (
                          <span className="text-info ms-2">{user.specialty}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </>
            ) : (
              <div className="text-center text-muted py-3 px-2">
                {searchTerm || selectedRole ? "No users found matching your criteria" : "No users available"}
              </div>
            )}
          </div>

          <Form.Text className="text-muted p-2">{helperText}</Form.Text>
        </Dropdown.Menu>
      </Dropdown>
    </Form.Group>
  );
};

export default ExpandableUserSelector;
